import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { loginAtom } from '../../../lib/auth';

import Check from '@mui/icons-material/Check';
import Launch from '@mui/icons-material/Launch';

import {
  Box,
  CircularProgress
} from '@mui/material';

import {
  Button,
  IconButton,
  Typography
} from '../styled';

type Props = {
  sx?: object;
  caption?: string;
  fileInputName?: string;
  onChange?: (e:string) => void;
  onChangeIsUploading?: (b:boolean) => void;
  // multi?: boolean;
  current?: string;
  accept?: string;
};

export const ChunkedFileUploader: FunctionComponent<Props> = ({sx, onChange, caption, fileInputName, accept, current, onChangeIsUploading}) => {
  const theme = useTheme();
  const [login, setLogin] = useAtom(loginAtom);
  const {t, i18n} = useTranslation(['translations']);

  const [file, setFile] = useState<File | undefined>();
  const [signedUrl, setSignedUrl] = useState<string>();
  const [perc, setPerc] = useState<number>(0);

  useEffect(() => {
    if(!!onChange && !!signedUrl) onChange(signedUrl);
  }, [signedUrl]);

  const uploadFile = async () => {
    if (!file) return;
    if (!login) return;

    try {
      let api_url = 'https://api.nutriportal.eu';
      if (!!(window.location.host.indexOf('localhost') > -1 || window.location.host.indexOf('staging.') > -1 || window.location.host.indexOf('myvita.link') > -1)) api_url = 'https://api-st.nutriportal.eu';
      // if (!!(window.location.host.indexOf('localhost') > -1 || window.location.host.indexOf('staging.') > -1)) api_url = 'http://localhost:3500';
      if (!!onChangeIsUploading) onChangeIsUploading(true);

      // Request a pre-signed URL from your backend
      const response = await axios.get(
        `${api_url}/en/v3/objects/generate_presigned_url?${new URLSearchParams({
          filename: file.name,
          filetype: file.type,
        })}`, {
          headers: {
            "Auth-Token": login.auth_token,
            "Auth-Email": login.email,
            "X-KANGA-KEY": "Dcps4WdQ0MYQw0R3", // ENV?
            "MYVITA": "1",
          }
        }
      );
      const url = response.data.data;

      // Upload the file using the pre-signed URL
      await axios.put(url, file, {
        headers: {
          "Content-Type": file.type,
        },
        onUploadProgress: (progressEvent) => {
          const percentage = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          setPerc(percentage);
        }
      });

      console.log("File uploaded successfully!");
      setSignedUrl(url.split("?")[0]);
      if (!!onChangeIsUploading) onChangeIsUploading(false);
    } catch (error) {
      if (!!onChangeIsUploading) onChangeIsUploading(false);
      console.error("Error uploading file:", error);
    }
  };

  return (<>
    {!!caption && <Typography variant="caption">{caption}</Typography>}
    <Box sx={{
      padding: 1,
      width: "100%",
      borderRadius: 18,
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'row'
    }} onClick={() => {
      // @ts-ignore
      if (!!document.getElementById(!!fileInputName ? fileInputName : "fileInput")) document.getElementById(!!fileInputName ? fileInputName : "fileInput").click();
    }}>
      <Box sx={{ position: 'relative', display: 'inline-flex', marginRight: 1 }}>
        <CircularProgress
          variant="determinate"
          value={100}
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            color: 'lightgrey',
            zIndex: 1200
          }} />
        <CircularProgress variant="determinate" value={perc} sx={{zIndex: 1201}} />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '40px'
          }}
        >
          {(!current || !!file) && perc < 100 && <Typography variant="caption">{`${Math.round(perc)}%`}</Typography>}
          {(((!!current && !file) || perc > 99) || (!!file && perc > 99)) && <Check color="primary" />}
        </Box>
      </Box>
      
      {!!file && <Typography variant="caption" sx={{
        marginTop: 'auto',
        marginBottom: 'auto'
      }}>
        {file.name}
      </Typography>}
      {!!current && !file && <Typography variant="caption" sx={{
        marginTop: 'auto',
        marginBottom: 'auto',
        wordBreak: 'break-word',
        maxWidth: "calc(100%-100px)"
      }}>
        {current.split("/").reverse()[0].replaceAll("%20", " ")}
      </Typography>}

      {(!!file && perc < 1) && <Button
        label={t("general.chunked_uploader.button.upload")}
        id="upload"
        onClick={() => {
          uploadFile();
        }}
        sx={{
          marginTop: 0,
          marginLeft: 'auto',
          height: '40px'
        }}
      />}

      {/* open if current */}

      {!!current && !file && <IconButton
        onClick={() => {
          window.open(current, "_BLANK")
        }}
        sx={{
          marginTop: 0,
          marginLeft: 'auto',
          marginRight: 1,
          border: '1px solid transparent',
          borderColor: theme.palette.primary.main,
          height: '40px'
        }}
      >
        <Launch color="primary" />
      </IconButton>}

      {(!file || (!!file && perc > 99)) && <Button
        label={t("general.chunked_uploader.button.pick")}
        id="pick"
        onClick={() => {
          setPerc(0);
          // @ts-ignore
          if (!!document.getElementById(!!fileInputName ? fileInputName : "fileInput")) document.getElementById(!!fileInputName ? fileInputName : "fileInput").click();
        }}
        sx={{
          marginTop: 0,
          marginLeft: (!!current && !file) ? 1 : 'auto',
          height: '40px'
        }}
      />}
    </Box>


    <input type="file" style={{display: 'none'}} id={!!fileInputName ? fileInputName : "fileInput"} onChange={(e) => {
      setPerc(0);
      setFile((e?.currentTarget?.files || [])[0]);
    }} accept={accept || "image/jpeg, image/png, image/jpg, image/gif, video/*, audio/*, text/*, application/*"} />
  </>);
}